import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { environment } from 'environments/environment';
import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  AnalyticsUserProperties
} from '@core/services/infra/analytics-events';
import { PostHogService } from '@core/services/infra/posthog.service';

type ConsentChoice = 'accepted' | 'rejected';

interface StoredConsent {
  choice: ConsentChoice;
  ts: number;
}

type GtagFn = (...args: unknown[]) => void;

interface GtagWindow extends Window {
  dataLayer?: unknown[];
  gtag?: GtagFn;
}

/**
 * localStorage key + TTL. Intentionally mirrors the landing site's
 * `cookies.js` so the wording/behaviour stays consistent, but the store is
 * per-origin — app.propor.io keeps its own decision, separate from propor.io.
 */
const STORAGE_KEY = 'proporio_cookie_consent_v1';
const EXPIRY_DAYS = 180;

/**
 * The app's analytics FAÇADE: it owns the visitor's consent decision and fans
 * every call out to the two transports — Google Analytics 4 (gtag.js, wired
 * here) and PostHog (see {@link PostHogService}).
 *
 * Keeping one façade is the reason adding PostHog touched no call site: the
 * ~20 places that already call `track()` / `identify()` reach both tools, and
 * there is exactly one place where "has the visitor accepted?" is answered.
 *
 * Behaviour:
 *  - GA4 is only active where `features.enableAnalytics` is true AND an
 *    `analytics.gaMeasurementId` is configured (prod only today). PostHog has
 *    its own independent gate, so it can be exercised on dev while GA stays
 *    off. With neither configured every method is an inert no-op — nothing is
 *    loaded in local dev, the emulator or the Karma suite.
 *  - When active, gtag is loaded once with consent DEFAULTED TO 'denied'. No
 *    analytics cookies are written until the visitor accepts; until then GA only
 *    sends cookieless modeled pings, which is what Google's EU guidance allows.
 *  - PostHog takes the stricter route: the library is not even downloaded
 *    before an explicit accept (it stores on the device and profiles
 *    behaviour). Events fired before that are dropped, not queued.
 *  - Being a SPA, the automatic `page_view` is disabled (`send_page_view:false`)
 *    and one is emitted manually on every router `NavigationEnd`.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly posthog = inject(PostHogService);

  /** The visitor's decision, or null while it is still pending. */
  readonly consent = signal<ConsentChoice | null>(null);

  private booted = false;
  private routerHooked = false;

  /** True when GA is configured for this build (prod-only today). */
  get enabled(): boolean {
    return !!environment.features?.enableAnalytics && this.measurementId.length > 0;
  }

  /**
   * Whether the visitor must be asked at all — i.e. whether ANY consent-gated
   * tool is configured for this build. The banner reads this rather than
   * {@link enabled}: PostHog can be switched on for dev while GA4 stays off,
   * and in that build the visitor still has to be asked.
   */
  get consentRequired(): boolean {
    return this.enabled || this.posthog.enabled;
  }

  private get measurementId(): string {
    return environment.analytics?.gaMeasurementId ?? '';
  }

  private get win(): GtagWindow {
    return window as unknown as GtagWindow;
  }

  /**
   * Boot gtag (Consent Mode v2, default denied) and start tracking SPA
   * navigations. Safe to call repeatedly; no-op when analytics is disabled.
   * Call once from the root component.
   */
  init(): void {
    if (!this.consentRequired) return;

    const stored = this.readConsent();
    this.consent.set(stored?.choice ?? null);

    if (this.enabled) {
      this.bootGtag();
      this.updateConsent(stored?.choice === 'accepted');
    }
    // PostHog is only ever loaded for a visitor who has already accepted, so a
    // returning visitor gets it back here and a new one does not.
    if (stored?.choice === 'accepted') void this.posthog.enable();
    this.hookRouter();
  }

  /** Visitor accepted analytics cookies. */
  accept(): void {
    this.writeConsent('accepted');
    this.consent.set('accepted');
    if (this.enabled) {
      this.bootGtag();
      this.updateConsent(true);
    }
    void this.posthog.enable();
    if (this.consentRequired) this.hookRouter();
  }

  /** Visitor declined non-essential cookies. */
  reject(): void {
    this.writeConsent('rejected');
    this.consent.set('rejected');
    if (this.enabled) this.updateConsent(false);
    this.posthog.disable();
  }

  /** Re-open the decision (entry point for a future "Cookie settings" link). */
  resetConsent(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* localStorage unavailable — ignore */
    }
    this.consent.set(null);
    if (this.enabled) this.updateConsent(false);
    this.posthog.disable();
  }

  // ── event & identity API ────────────────────────────────────────────────────

  /**
   * Emit a GA4 custom event (typed against the {@link AnalyticsEventMap}
   * catalogue). No-op when analytics is disabled or gtag has not loaded.
   * Consent Mode governs storage: events still flow as cookieless modeled pings
   * until the visitor accepts, so this is always safe to call and never throws.
   */
  track<K extends AnalyticsEventName>(name: K, params?: AnalyticsEventMap[K]): void {
    // PostHog first, and outside the GA gate: the two tools are configured
    // independently, so a build with PostHog but no GA must still emit.
    this.posthog.capture(name, params as Record<string, unknown> | undefined);
    if (!this.enabled) return;
    const w = this.win;
    if (!w.gtag) return;
    try {
      w.gtag('event', name, params ?? {});
    } catch {
      /* analytics must never break a user flow */
    }
  }

  /**
   * Associate subsequent hits with a stable, pseudonymous user (Firebase uid)
   * for cross-device stitching and better Ads modelling. Re-uses the existing
   * SPA config (keeps `send_page_view:false` so this never double-counts views).
   */
  identify(uid: string | null | undefined): void {
    if (!uid) return;
    this.posthog.identify(uid);
    if (!this.enabled) return;
    const w = this.win;
    if (!w.gtag) return;
    w.gtag('config', this.measurementId, { user_id: uid, send_page_view: false });
  }

  /**
   * Bind subsequent events to the current workspace. PostHog-only: it models
   * this as a "group", which is what makes per-tenant usage readable. GA4 has
   * no equivalent — workspace shape is already carried there as the
   * `workspace_type` user property.
   */
  setWorkspace(workspaceId: string | null | undefined): void {
    if (!workspaceId) return;
    this.posthog.group(workspaceId);
  }

  /** Set user-scoped custom dimensions (workspace_type, plan_tier, …). No PII. */
  setUserProperties(props: AnalyticsUserProperties): void {
    this.posthog.setPersonProperties(props as Record<string, unknown>);
    if (!this.enabled) return;
    const w = this.win;
    if (!w.gtag) return;
    const clean: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(props)) {
      if (v !== undefined && v !== null && v !== '') clean[k] = v;
    }
    if (Object.keys(clean).length === 0) return;
    w.gtag('set', 'user_properties', clean);
  }

  /**
   * Enhanced Conversions: hand Google the visitor's email so it can match Ads
   * conversions far more reliably. Only sent once advertising cookies are
   * accepted (`ad_user_data` granted); gtag hashes it (SHA-256) in-browser —
   * the address never leaves the device in clear. Call right before the
   * conversion event (sign_up / purchase).
   */
  setUserData(email: string | null | undefined): void {
    if (!this.enabled || !email) return;
    if (this.consent() !== 'accepted') return;
    const w = this.win;
    if (!w.gtag) return;
    w.gtag('set', 'user_data', { email: email.trim().toLowerCase() });
  }

  /**
   * Resolve the GA4 client id (the `_ga` cookie identifier). Pass it to the
   * backend at checkout so the Stripe webhook can attribute a server-side
   * `purchase` (Measurement Protocol) to the SAME GA session/user. Resolves
   * null when analytics is off or gtag hasn't answered within 1s.
   */
  getClientId(): Promise<string | null> {
    return new Promise((resolve) => {
      if (!this.enabled) {
        resolve(null);
        return;
      }
      const w = this.win;
      if (!w.gtag) {
        resolve(null);
        return;
      }
      let settled = false;
      const done = (v: string | null): void => {
        if (settled) return;
        settled = true;
        resolve(v);
      };
      try {
        w.gtag('get', this.measurementId, 'client_id', (id: unknown) =>
          done(typeof id === 'string' ? id : null)
        );
        setTimeout(() => done(null), 1000);
      } catch {
        done(null);
      }
    });
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /** Opt-in GA4 DebugView, toggled via `localStorage['proporio_ga_debug']==='1'`. */
  private get debug(): boolean {
    try {
      return localStorage.getItem('proporio_ga_debug') === '1';
    } catch {
      return false;
    }
  }

  private bootGtag(): void {
    if (this.booted) return;
    this.booted = true;

    const id = this.measurementId;
    const w = this.win;
    const dataLayer = (w.dataLayer = w.dataLayer ?? []);
    const gtag: GtagFn = (...args: unknown[]): void => {
      dataLayer.push(args);
    };
    w.gtag = w.gtag ?? gtag;

    w.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
      wait_for_update: 500
    });
    w.gtag('js', new Date());
    // SPA: suppress the automatic page_view; we emit them on NavigationEnd.
    // `debug_mode` (opt-in via localStorage) surfaces hits in GA4 DebugView.
    w.gtag('config', id, {
      anonymize_ip: true,
      send_page_view: false,
      ...(this.debug ? { debug_mode: true } : {})
    });

    const s = document.createElement('script');
    s.async = true;
    s.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(id);
    document.head.appendChild(s);
  }

  private updateConsent(granted: boolean): void {
    const w = this.win;
    if (!w.gtag) return;
    const value: 'granted' | 'denied' = granted ? 'granted' : 'denied';
    // On acceptance we grant the Google Ads signals too, so conversion import
    // + Enhanced Conversions + remarketing work. On rejection everything stays
    // denied and GA only sends cookieless modeled pings.
    w.gtag('consent', 'update', {
      analytics_storage: value,
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value
    });
  }

  private hookRouter(): void {
    if (this.routerHooked) return;
    this.routerHooked = true;

    // If the first navigation already completed before we subscribed, count it.
    if (this.router.navigated) {
      this.sendPageView(this.router.url);
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((e) => this.sendPageView(e.urlAfterRedirects));
  }

  private sendPageView(path: string): void {
    // Path only — never the query string. Our routes carry ids and a query
    // string is where tokens end up (mirrors PostHog's property_denylist).
    this.posthog.capturePageView(path.split(/[?#]/)[0]);
    const w = this.win;
    if (!w.gtag) return;
    w.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: document.title
    });
  }

  private readConsent(): StoredConsent | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw) as StoredConsent | null;
      if (!data || !data.ts) return null;
      const ageDays = (Date.now() - data.ts) / 86_400_000;
      if (ageDays > EXPIRY_DAYS) return null;
      return data;
    } catch {
      return null;
    }
  }

  private writeConsent(choice: ConsentChoice): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice, ts: Date.now() }));
    } catch {
      /* localStorage unavailable — ignore */
    }
  }
}
